import React, { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Bold,
  Italic,
  Heading2,
  Heading3,
  Quote,
  List,
  Link as LinkIcon,
  Unlink,
  Undo2,
  Redo2,
  Image as ImageIcon,
  Loader2,
} from "lucide-react";

const TbBtn = ({ active, onClick, title, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    data-testid={testId}
    className={`p-2 border border-rule transition-colors ${
      active
        ? "bg-ink text-paper border-ink"
        : "bg-paper text-ink-soft hover:text-ink hover:border-ink"
    }`}
  >
    {children}
  </button>
);

export default function RichTextEditor({ value, onChange, placeholder }) {
  const fileInputRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { class: "underline text-terracotta" },
      }),
      Image.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          class: "my-6 mx-auto max-w-full h-auto block border border-rule",
        },
      }),
      Placeholder.configure({
        placeholder: placeholder || "Begin writing…",
      }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
    editorProps: {
      attributes: {
        "data-testid": "editor-content-input",
        class:
          "prose-editorial min-h-[360px] outline-none px-4 py-3 bg-paper border border-rule focus:border-ink",
      },
    },
  });

  // Sync external value (e.g., when loading an existing post)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || "") !== current) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, value]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const handleImageFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be 8MB or smaller.");
      return;
    }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.url) {
        editor.chain().focus().setImage({ src: data.url, alt: file.name }).run();
        toast.success("Image inserted");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div data-testid="rich-text-editor">
      <div className="flex flex-wrap gap-1 mb-2">
        <TbBtn
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold"
          testId="rte-bold"
        >
          <Bold size={14} />
        </TbBtn>
        <TbBtn
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic"
          testId="rte-italic"
        >
          <Italic size={14} />
        </TbBtn>
        <TbBtn
          active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          title="Heading 2"
          testId="rte-h2"
        >
          <Heading2 size={14} />
        </TbBtn>
        <TbBtn
          active={editor.isActive("heading", { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          title="Heading 3"
          testId="rte-h3"
        >
          <Heading3 size={14} />
        </TbBtn>
        <TbBtn
          active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          title="Blockquote"
          testId="rte-blockquote"
        >
          <Quote size={14} />
        </TbBtn>
        <TbBtn
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Bulleted list"
          testId="rte-bullet"
        >
          <List size={14} />
        </TbBtn>
        <TbBtn
          active={editor.isActive("link")}
          onClick={setLink}
          title="Link"
          testId="rte-link"
        >
          <LinkIcon size={14} />
        </TbBtn>
        <TbBtn
          onClick={() => editor.chain().focus().unsetLink().run()}
          title="Remove link"
          testId="rte-unlink"
        >
          <Unlink size={14} />
        </TbBtn>
        <TbBtn
          onClick={() => !uploadingImage && fileInputRef.current?.click()}
          title="Insert image"
          testId="rte-image"
        >
          {uploadingImage ? (
            <Loader2 className="animate-spin" size={14} />
          ) : (
            <ImageIcon size={14} />
          )}
        </TbBtn>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          data-testid="rte-image-file-input"
          onChange={(e) => handleImageFile(e.target.files?.[0])}
        />
        <div className="flex-1" />
        <TbBtn
          onClick={() => editor.chain().focus().undo().run()}
          title="Undo"
          testId="rte-undo"
        >
          <Undo2 size={14} />
        </TbBtn>
        <TbBtn
          onClick={() => editor.chain().focus().redo().run()}
          title="Redo"
          testId="rte-redo"
        >
          <Redo2 size={14} />
        </TbBtn>
      </div>

      <EditorContent editor={editor} />
    </div>
  );
}
